import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlobalContext, ReducerActionType } from '../../GlobalReducer';
import { ReducerStateInterface } from '../../interfaces/core/ReducerStateInterface';
import { useQuery } from '@tanstack/react-query';
import { getProduct } from '../../api/catalog';
import { Button, Spinner, Alert } from 'react-bootstrap';

interface SharedBasketData {
    name: string;
    items: Array<{
        id: number;
        quantity: number;
    }>;
}

const SharedBasket: React.FC = () => {
    const { data: encodedData } = useParams<{ data: string }>();
    const navigate = useNavigate();
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);

    const [basketData, setBasketData] = useState<SharedBasketData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadedProducts, setLoadedProducts] = useState<any[]>([]);

    useEffect(() => {
        if (!encodedData) {
            setError('Некорректная ссылка для шеринга корзины');
            setIsLoading(false);
            return;
        }

        try {
            const jsonString = decodeURIComponent(atob(encodedData));
            const data: SharedBasketData = JSON.parse(jsonString);

            if (!data.name || !Array.isArray(data.items) || data.items.length === 0) {
                setError('Некорректный формат данных корзины');
                setIsLoading(false);
                return;
            }

            setBasketData(data);
        } catch (err) {
            console.error('Failed to decode basket data:', err);
            setError('Не удалось декодировать данные корзины');
            setIsLoading(false);
        }
    }, [encodedData]);

    useEffect(() => {
        if (!basketData) return;

        const loadProducts = async () => {
            try {
                const productPromises = basketData.items.map(item =>
                    getProduct(item.id.toString())
                );

                const products = await Promise.all(productPromises);
                setLoadedProducts(products.map((product, index) => ({
                    product,
                    quantity: basketData.items[index].quantity
                })));
                setIsLoading(false);
            } catch (err) {
                console.error('Failed to load products:', err);
                setError('Не удалось загрузить данные о товарах');
                setIsLoading(false);
            }
        };

        loadProducts();
    }, [basketData]);

    const handleAddToBasket = () => {
        if (!loadedProducts.length) return;

        loadedProducts.forEach(({ product, quantity }) => {
            dispatch({
                type: ReducerActionType.ADD_TO_BASKET,
                payload: {
                    ...product,
                    quantity
                }
            });
        });

        navigate('/basket');
    };

    const handleReplaceBasket = () => {
        if (!loadedProducts.length) return;

        dispatch({ type: ReducerActionType.CLEAR_BASKET });

        loadedProducts.forEach(({ product, quantity }) => {
            dispatch({
                type: ReducerActionType.ADD_TO_BASKET,
                payload: {
                    ...product,
                    quantity
                }
            });
        });

        navigate('/basket');
    };

    if (isLoading) {
        return (
            <div className="container text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </Spinner>
                <p className="mt-3">Загружаем данные корзины...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <Alert variant="danger">
                    <Alert.Heading>Ошибка</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button variant="outline-danger" onClick={() => navigate('/')}>
                            На главную
                        </Button>
                    </div>
                </Alert>
            </div>
        );
    }

    if (!basketData) {
        return null;
    }

    return (
        <div className="container mt-5">
            <div className="card">
                <div className="card-header">
                    <h2>Поделились корзиной: {basketData.name}</h2>
                </div>
                <div className="card-body">
                    <p className="card-text">
                        В корзине {basketData.items.length} товар(ов)
                    </p>

                    {loadedProducts.length > 0 && (
                        <div className="mt-4">
                            <h5>Список товаров:</h5>
                            <ul className="list-group mb-4">
                                {loadedProducts.map(({ product, quantity }) => (
                                    <li key={product.id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{product.name}</strong>
                                            <br />
                                            <small className="text-muted">Артикул: {product.code}</small>
                                        </div>
                                        <span className="badge bg-primary rounded-pill">
                                            {quantity} шт.
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="d-flex gap-3">
                        <Button
                            variant="primary"
                            onClick={handleReplaceBasket}
                            className="flex-fill"
                        >
                            Заменить мою корзину
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={handleAddToBasket}
                            className="flex-fill"
                        >
                            Добавить к моей корзине
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SharedBasket;
